// @flow

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Alert, Button, Icon, InputGroup, Intent } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { IconNames } from '@blueprintjs/icons';
import { PanelHeader } from '../../panel';
import { ThreadDownloadActions } from '../../ai-prompt-management';
import { usePromptChatState } from '../../ai-prompt-management';
import { hasDownloadableContent } from '../../ai-prompt-management/utils/threadDownloadUtils';
import { errorToast } from '../../../utils/toaster';
import { validateThreadTitle } from '../../../utils/chat-title';

// Match AI Reports CompactPanelHeader styling with rounded corners
const CompactPanelHeader = styled(PanelHeader).withConfig({
  componentId: 'ai-chat-topbar-compact',
})`
  padding: 0.75rem 1rem;
  border-bottom-left-radius: var(--widget-border-radius);
  border-bottom-right-radius: var(--widget-border-radius);
`;

const HeaderRow = styled.div`
  display: flex;
  /* Centre so a single-line title lines up with the buttons; a wrapped title
     stays centred against the button group (flex-wrap removed so the buttons
     never drop to a new row). */
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  font-family: var(--fonts-heading);
  color: var(--widget-heading-text);
  display: flex;
  align-items: center;
  min-width: 0;
`;

const TitleIcon = styled(Icon)`
  margin-right: 0.4rem;
  color: var(--widget-heading-text);
`;

const TitleText = styled.span`
  min-width: 0;
  /* Wrap long titles onto multiple lines; break unbroken strings so the row
     never overflows and the right-hand buttons stay put. */
  overflow-wrap: anywhere;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`;

const TitlePrefix = styled.span`
  white-space: nowrap;
  margin-right: 0.4rem;
`;

const EditRow = styled.div`
  display: flex;
  /* Top-align so the validation message can hang below the input without
     nudging the input off the prefix's centre line. */
  align-items: flex-start;
  min-width: 0;
  flex: 1 1 auto;
`;

// Edit-mode "Ask Aura - " prefix; fixed height matches the input so their
// centre lines stay aligned under flex-start.
const EditPrefix = styled(Title)`
  height: 34px;
  flex: 0 0 auto;
`;

const NameBox = styled.div`
  /* Column: input on top, validation message below. Fills the space between
     the prefix and the Save/Cancel buttons; shrinks to min-width on narrow
     screens (HeaderRow wraps). */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 12ch;
`;

const ShrinkInput = styled(InputGroup)`
  width: 100%;
  .bp3-input {
    /* Match the Title (h2) typography so the input reads as the title and
       its text centre line stays aligned with the "Ask Aura - " prefix */
    font-size: 18px;
    font-weight: 400;
    font-family: var(--fonts-heading);
    color: var(--widget-heading-text);
    height: 34px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .bp3-input:focus {
    text-overflow: clip; /* show full content while editing */
    overflow: auto; /* allow horizontal scroll if long */
  }
`;

const ValidationMessage = styled.div`
  color: #c23030;
  font-size: 12px;
  margin-top: 0.25rem;
  /* Align the message text with the input text (bp3-input has 10px l/r padding) */
  padding-left: 10px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  /* Keep the buttons on the right at their natural width while the title wraps */
  flex: 0 0 auto;
`;

type TopBarProps = {
  onNew: () => void,
  onDelete?: () => Promise<void> | void,
  newDisabled?: boolean,
  threadTitle?: string,
  onRenameSave?: (newTitle: string) => Promise<void>, // rejects on failure
};

const TopBar = ({ onNew, onDelete, newDisabled, threadTitle, onRenameSave }: TopBarProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputElRef = useRef(null);
  const state = usePromptChatState();

  const canDelete = useMemo(() => {
    if (!state) return false;
    if (state.isStreaming) return false;
    return hasDownloadableContent(state.messages || []);
  }, [state]);

  const deleteDisabled = !onDelete || !canDelete || isDeleting;

  // Rename is available only once the thread has a title (guarantees it exists
  // server-side), a save handler is provided, and no response is streaming (D6).
  const renameAvailable = !!(
    threadTitle &&
    onRenameSave &&
    !(state && state.isStreaming)
  );

  const validationError = useMemo(
    () => (isEditing ? validateThreadTitle(draftTitle) : null),
    [isEditing, draftTitle],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!onDelete) return;
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [onDelete, isDeleting]);

  const enterEditMode = useCallback(() => {
    if (!renameAvailable) return;
    setDraftTitle(threadTitle || '');
    setIsEditing(true);
  }, [renameAvailable, threadTitle]);

  const exitEditMode = useCallback(() => {
    setIsEditing(false);
    setDraftTitle('');
  }, []);

  // Focus + select-all when entering edit mode.
  useEffect(() => {
    if (isEditing && inputElRef.current) {
      inputElRef.current.focus();
      inputElRef.current.select();
    }
  }, [isEditing]);

  const handleSave = useCallback(async () => {
    if (isSaving) return;
    if (validateThreadTitle(draftTitle)) return;
    const trimmed = draftTitle.trim();
    if (trimmed === threadTitle) {
      exitEditMode(); // unchanged after trim — no API call (D5)
      return;
    }
    if (!onRenameSave) return;
    setIsSaving(true);
    try {
      await onRenameSave(trimmed);
      setIsEditing(false);
      setDraftTitle('');
    } catch (e) {
      errorToast({
        message: 'Unable to update conversation. Please try again later.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [draftTitle, exitEditMode, isSaving, onRenameSave, threadTitle]);

  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        exitEditMode();
      }
    },
    [handleSave, exitEditMode],
  );

  const displayTitle = threadTitle ? `Ask Aura - ${threadTitle}` : 'Ask Aura';

  return (
    <CompactPanelHeader>
      <HeaderRow>
        {isEditing ? (
          <LeftSection>
            <EditRow>
              <EditPrefix>
                <TitleIcon icon={IconNames.CHAT} size={18} />
                <TitlePrefix>Ask Aura - </TitlePrefix>
              </EditPrefix>
              <NameBox>
                <ShrinkInput
                  inputRef={(el) => {
                    inputElRef.current = el;
                  }}
                  value={draftTitle}
                  maxLength={128}
                  disabled={isSaving}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                />
                {validationError && (
                  <ValidationMessage>{validationError}</ValidationMessage>
                )}
              </NameBox>
            </EditRow>
          </LeftSection>
        ) : (
          <LeftSection>
            <Title>
              <TitleIcon icon={IconNames.CHAT} size={18} />
              {threadTitle && renameAvailable ? (
                <Tooltip2
                  content="Click title to rename chat"
                  position="bottom"
                  openOnTargetFocus={false}
                >
                  <TitleText clickable onClick={enterEditMode}>
                    {displayTitle}
                  </TitleText>
                </Tooltip2>
              ) : (
                <TitleText>{displayTitle}</TitleText>
              )}
            </Title>
          </LeftSection>
        )}
        {isEditing ? (
          <RightSection>
            <Tooltip2
              content="Save title update"
              position="bottom"
              openOnTargetFocus={false}
            >
              <span>
                <Button
                  icon={IconNames.FLOPPY_DISK}
                  text="Save"
                  intent={Intent.PRIMARY}
                  onClick={handleSave}
                  disabled={isSaving || !!validationError}
                />
              </span>
            </Tooltip2>
            <Tooltip2
              content="Cancel title update"
              position="bottom"
              openOnTargetFocus={false}
            >
              <span>
                <Button
                  icon={IconNames.UNDO}
                  text="Cancel"
                  onClick={exitEditMode}
                  disabled={isSaving}
                />
              </span>
            </Tooltip2>
          </RightSection>
        ) : (
          <RightSection>
            <ThreadDownloadActions
              headingTitle={threadTitle}
              headingFallback="Ask Aura Conversation"
            />
            <Tooltip2
              content="Clears current conversation and starts a new chat"
              position="bottom"
              openOnTargetFocus={false}
            >
              <Button
                icon={IconNames.REFRESH}
                text="New"
                intent={Intent.PRIMARY}
                onClick={onNew}
                disabled={newDisabled}
              />
            </Tooltip2>
            <Tooltip2
              content="Delete this conversation"
              position="bottom"
              openOnTargetFocus={false}
            >
              <span>
                <Button
                  icon={IconNames.TRASH}
                  intent={Intent.DANGER}
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleteDisabled}
                >
                  Delete
                </Button>
              </span>
            </Tooltip2>
          </RightSection>
        )}
      </HeaderRow>
      <Alert
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        icon="trash"
        intent={Intent.DANGER}
        isOpen={!!showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
      >
        <p>Are you sure you want to delete this conversation?</p>
      </Alert>
    </CompactPanelHeader>
  );
};

export default TopBar;
